import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import Skeleton from '@mui/material/Skeleton';
import { useSWRConfig } from 'swr';
import { useTeamsSummary } from '../../../../lib/api/swrApi';
import { HeadingLevel } from '../../../../lib/utils/HeadingMapper';
import styles from './TeamAssignedTrainings.module.scss';
import { PureSeparator } from '../Separator/Separator';
import Layout from '../../../common/Layout/Layout';
import useDatePicker from '../../../../lib/hooks/useDatePicker';
import TeamAssignedTrainingsResults from './TeamAssignedTrainingsResults';
import Dropdown, { DropdownItem } from '../../../common/Dropdown/Dropdown';
import Button from '../../../common/Button/Button';
import useBackendTable from '../../../../lib/hooks/useBackendTable';
import { getInitialDataFromHashParams } from './TeamAssignedTrainings.utils';
import { Label } from '../../../common/Label/Label';
import { TEAM_ASSIGNED_TRAININGS_LABELS } from '../../../../lib/constants/labelKeys';
import { TeamAssignedTrainingsProps } from './TeamAssignedTrainings.types';
import { DateShortcuts } from '../../base/DateRangePicker/DateRangePicker.types';
import useHashParamsState from '../../../../lib/hooks/useHashParamsState';
import HeadingWithIcon from '../../../common/HeadingWithIcon/HeadingWithIcon';
import ErrorInfo from '../../base/ErrorInfo/ErrorInfo';
import LazyDateRangePicker from '../../base/DateRangePicker/LazyDateRangePicker';
import useFeature from '../../../../lib/hooks/useFeature';
import TeamAssignTrainingDialog from './TeamAssignTrainingDialog';
import { useSnackbarContext } from '../../../../lib/context/SnackbarContext';
import { NotificationType } from '../../base/Notification/Notification';
import { classes } from '../../../../lib/utils/cssUtils';
import { replaceObjectPlaceholders } from '../../../../lib/utils/placeholders/replacePlaceholder';
import { FeatureToggleCodename } from '../../../../lib/types/enums';

const TitleRowSkeleton = () => {
  return <Skeleton className={styles.titleRowSkeleton} />;
};

const TeamAssignedTrainings = ({
  heading,
  headingLevel,
  icon,
  teamSizeWarningMessage,
  ...rest
}: TeamAssignedTrainingsProps) => {
  const dateDisabled = useFeature(FeatureToggleCodename.teamCourseAssignmentDateDisabled);
  const assignmentEnabled = useFeature(FeatureToggleCodename.teamTrainingAssignment);
  const { dialogTitle, successMessage, buttonLabel, noItemsSelectedMessage, ...resultsProps } = rest;
  const today = dateDisabled ? undefined : dayjs();
  const { hash, updateHashParams } = useHashParamsState();
  const { teamId: hashTeamId, ...restHashInitial } = useMemo(
    () => getInitialDataFromHashParams({ hash, pageSize: rest.pageSize, today }),
    [],
  );
  const picker = useDatePicker({ startDate: dayjs(restHashInitial.since), endDate: dayjs(restHashInitial.to) });
  const { startDate, endDate } = picker;
  const { data, isLoading, error } = useTeamsSummary();
  const { mutate } = useSWRConfig();
  const [localSelectedTeam, selectLocalTeam] = useState<string | null>(hashTeamId);
  const [selectedTeam, selectTeam] = useState<string | null>(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const { triggerSnackbar } = useSnackbarContext();
  const [state, dispatch] = useBackendTable<'/teams/{teamid}/training/summary'>(restHashInitial);
  const filtersChanged =
    localSelectedTeam !== selectedTeam ||
    !startDate?.isSame(dayjs(state.since), 'day') ||
    !endDate?.isSame(dayjs(state.to), 'day');

  const handleTeamChange = (teamId: string) => {
    selectLocalTeam(teamId);
  };

  const handleUpdateClick = (teamId: string) => {
    selectTeam(teamId);
    selectLocalTeam(teamId);

    if (startDate && endDate && !dateDisabled) {
      dispatch({ type: 'dateRangeChange', payload: { since: startDate, to: endDate } });
    }
  };

  const handleAssignSuccess = (fullSuccess?: boolean) => {
    if (fullSuccess) {
      setShowAssignModal(false);
      triggerSnackbar(successMessage, NotificationType.Success);
    }

    mutate(([key]: [string]) => key.endsWith(`/teams/${selectedTeam}/training/summary`));
  };

  useLayoutEffect(() => {
    if (data === undefined) {
      return;
    }

    if (localSelectedTeam) {
      selectTeam(localSelectedTeam);

      return;
    }

    const selected = data[0]?.id;

    if (selected) {
      selectLocalTeam(selected);
      selectTeam(selected);
    }
  }, [data]);

  useEffect(() => {
    if (selectedTeam) {
      updateHashParams({ ...state, teamid: selectedTeam });
    }
  }, [state, selectedTeam]);

  if (error) {
    return <ErrorInfo error={error} internal />;
  }

  return (
    <Layout>
      <HeadingWithIcon level={HeadingLevel.h2} as={headingLevel} icon={icon}>
        {heading}
      </HeadingWithIcon>
      <PureSeparator className={styles.separator} />
      <div>
        <Label name={TEAM_ASSIGNED_TRAININGS_LABELS.SHOW_ASSIGNED_TITLE} className={styles.showAssignedLabel} />
        <div className={classes(styles.filteringRow, { [styles.filteringRowWithAssignment]: assignmentEnabled })}>
          {isLoading ? (
            <TitleRowSkeleton />
          ) : (
            <>
              <Dropdown
                value={localSelectedTeam ?? ''}
                onChange={event =>
                  dateDisabled ? handleUpdateClick(event.target.value) : handleTeamChange(event.target.value)
                }
                classes={{ select: styles.dropdownSelect, root: styles.dropdownRoot }}
              >
                {data?.map(({ id, teamPathNames }) => (
                  <DropdownItem key={id} value={id}>
                    {teamPathNames}
                  </DropdownItem>
                ))}
              </Dropdown>
              {!dateDisabled && (
                <>
                  <Label name={TEAM_ASSIGNED_TRAININGS_LABELS.FILTERS_INFIX} className={styles.inter} />
                  <LazyDateRangePicker
                    {...picker}
                    buttonClasses={styles.datePicker}
                    shortcuts={[DateShortcuts.Last7Days, DateShortcuts.Last30Days, DateShortcuts.ThisYear]}
                  />
                  <Button
                    onClick={() => handleUpdateClick(localSelectedTeam ?? '')}
                    className={styles.updateButton}
                    disabled={!filtersChanged}
                  >
                    <Label name={TEAM_ASSIGNED_TRAININGS_LABELS.UPDATE_BUTTON} />
                  </Button>
                </>
              )}
              {assignmentEnabled && (
                <Button className={styles.assignTrainingButton} onClick={() => setShowAssignModal(true)}>
                  {buttonLabel}
                </Button>
              )}
            </>
          )}
        </div>
      </div>
      <TeamAssignedTrainingsResults
        state={state}
        dispatch={dispatch}
        teamId={selectedTeam}
        isTeamsLoading={isLoading}
        {...resultsProps}
      />
      {assignmentEnabled && showAssignModal && !!selectedTeam && (
        <TeamAssignTrainingDialog
          dialogTitle={replaceObjectPlaceholders(data?.find(team => team.id === selectedTeam) ?? {}, dialogTitle)}
          isModalOpened
          noItemsSelectedMessage={noItemsSelectedMessage}
          onCancel={() => setShowAssignModal(false)}
          onSuccess={handleAssignSuccess}
          teamId={selectedTeam}
          totalMembers={data?.find(team => team.id === selectedTeam)?.totalMembers}
          teamSizeWarningMessage={teamSizeWarningMessage}
        />
      )}
    </Layout>
  );
};

export default TeamAssignedTrainings;
